/**
 * Custom override of core Navigation.
 *
 * The top-category tree is fetched ONCE on the server and cached as the raw
 * `CategoryType[]` under a single key (DATA_KEY_NAVIGATION). Both client hooks
 * read that one entry from the SWR fallback and apply their own projection in
 * `useMemo`, so the response is cached once and never re-requested on the client:
 *   - `useNavigation`    -> `dataMap`           -> `PageLink[]`
 *   - `useGetCategories` -> `Categories[]`  (see shared/custom/hooks/Categories.ts)
 *
 * This is a copy of core `getNavigation` with two changes vs. core:
 *   1. it caches the RAW response (not the `PageLink` projection) so the entry
 *      can be reused by `useGetCategories`; and
 *   2. `useNavigation` is overridden here to map that raw entry client-side.
 * The private `dataMap`/`fetcher` helpers are duplicated from core because they
 * are not exported. Keep in sync with core on upgrade.
 *
 * @see shared/custom/hooks/Categories.ts
 */
import { getFlexFlowStoreFeature } from '@/data/Content/FlexFlowStoreFeature-Server';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { getStoreLocale } from '@/data/Content/StoreLocale-Server';
import { cacheCategories } from '@/data/Content/_Category';
import { categoryFetcher } from '@/data/Content/_CategoryFetcher';
import { getLocalization, useLocalization } from '@/data/Localization';
import { getSettings, useSettings } from '@/data/Settings';
import { getUser, useUser } from '@/data/User';
import { getServerCacheScope } from '@/data/cache/getServerCacheScope';
import { TOP_CATEGORIES_DEPTH_LIMIT } from '@/data/config/TOP_CATEGORIES_DEPTH_LIMIT';
import { DATA_KEY_NAVIGATION } from '@/data/constants/dataKey';
import { EMS_STORE_FEATURE } from '@/data/constants/flexFlowStoreFeature';
import { Cache } from '@/data/types/Cache';
import { CategoryType } from '@/data/types/Category';
import { constructRequestParamsWithPreviewToken } from '@/data/utils/constructRequestParams';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { getContractIdParamFromContext } from '@/data/utils/getContractIdParamFromContext';
import { getRequestId } from '@/data/utils/getRequestId';
import { getServerSideCommon } from '@/data/utils/getServerSideCommon';
import { expand, shrink } from '@/data/utils/keyUtil';
import { laggyMiddleWare } from '@/data/utils/laggyMiddleWare';
import { traceWithId } from '@/data/utils/loggerUtil';
import { RequestParams } from 'integration/generated/query/http-client';
import { GetServerSidePropsContext } from 'next';
import { useMemo } from 'react';
import useSWR, { unstable_serialize as unstableSerialize } from 'swr';

export type PageLink = {
	label: string;
	url?: string;
	children: PageLink[];
	trail?: string[];
};

const dataMap = (contents: CategoryType[] = [], trail: string[] = []): PageLink[] =>
	contents.map(({ name, seo, children, uniqueID }) => ({
		label: name,
		url: seo?.href || '',
		children: dataMap(children, [...trail, uniqueID, name, seo.href]),
		trail,
	}));

const fetcher =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	async (props: any, params: RequestParams): Promise<CategoryType[]> =>
		(await categoryFetcher(pub, context)(props, params)) ?? [];

export const getNavigation = async (cache: Cache, context: GetServerSidePropsContext) => {
	traceWithId(getRequestId(context), 'getNavigation: start');
	await getFlexFlowStoreFeature({ cache, id: EMS_STORE_FEATURE.HIDDEN_DOM_SSR, context });
	const settings = await getSettings(cache, context);
	const user = await getUser(cache, context);
	const { localeName: locale } = await getStoreLocale({ cache, context });
	await getLocalization(cache, locale, 'AllCategoriesExpandedMenu');
	const routes = await getLocalization(cache, locale, 'Routes');
	const { storeId, langId } = getServerSideCommon(settings, context);
	const props = {
		storeId,
		depthAndLimit: TOP_CATEGORIES_DEPTH_LIMIT,
		...getContractIdParamFromContext(user.context),
		langId,
	};
	// Same props/serialization as both client hooks, so this single entry seeds
	// their SWR fallback and prevents a redundant client fetch of the same tree.
	const key = unstableSerialize([shrink(props), DATA_KEY_NAVIGATION]);
	const params = constructRequestParamsWithPreviewToken({ context, settings, routes });
	const cacheScope = getServerCacheScope(context, user.context);
	const cacheValue = await cache.get(key, cacheScope);
	if (cacheValue) {
		cache.set(key, Promise.resolve(cacheValue), cacheScope);
		traceWithId(getRequestId(context), 'getNavigation: end (used cache)');
		return;
	}
	const rawValue = await fetcher(false, context)(props, params);

	traceWithId(getRequestId(context), 'cacheCategories: start');
	cacheCategories(cache, rawValue, settings, user.context, cacheScope);
	traceWithId(getRequestId(context), 'cacheCategories: end');

	// Cache the RAW response once; each client hook maps it itself.
	cache.set(key, Promise.resolve(rawValue), cacheScope);

	traceWithId(getRequestId(context), 'getNavigation: end (filled cache)');
};

export const useNavigation = () => {
	const router = useNextRouter();
	const { settings } = useSettings();
	const { storeId, langId } = getClientSideCommon(settings, router);
	const { AllCategoriesLabel } = useLocalization('AllCategoriesExpandedMenu');
	const { user } = useUser();
	const params = useExtraRequestParameters();
	const { data, error } = useSWR(
		storeId
			? [
					shrink({
						storeId,
						depthAndLimit: TOP_CATEGORIES_DEPTH_LIMIT,
						...getContractIdParamFromContext(user?.context),
						langId,
					}),
					DATA_KEY_NAVIGATION,
			  ]
			: null,
		// raw fetch shared with `useGetCategories`; projection happens below
		async ([props]) => fetcher(true)(expand(props), params),
		{ use: [laggyMiddleWare], revalidateIfStale: false }
	);
	const navigation = useMemo(() => {
		const tree = dataMap(data);
		return [{ label: AllCategoriesLabel.t(), children: tree }, ...tree] as PageLink[];
	}, [AllCategoriesLabel, data]);
	return {
		navigation,
		loading: !error && !data,
		error,
	};
};
